import OlMap from "ol/Map";

import GlobalSettings from "lib/interfaces/globalsettings";
import EventSystem from "lib/classes/eventsystem";
import UIManager from "lib/classes/uimanager";
import MapToolMode from "lib/enums/maptoolmode";

import { hostMatch } from "lib/utilities/network";

import Lang from "lib/localization/lang";
import { getLang } from "lib/localization/vendor";

import QueryString from "query-string";
import { v4 as uuidv4 } from "uuid";

/**
	* Global variable structure
	*
	* @param _config		-	Main configuration class instance
	* @param _dimensions	-	Dimensions object containing screen size (assigned in views/App) ({width, height})
	* @param _events		-	Global events system for core cross app interaction
	* @param _ui			-	Global user interface manager
*/
declare global {
	var _config:GlobalSettings;
	var _dimensions:any;
	var _events:EventSystem;
	var _ui:UIManager;
	var _hash:string; // Start window hash
	var _hashed:any; // Serialized start window hash
}

const urlParams:any = QueryString.parse(location.search);
const uuid:string = urlParams.siteid?.replace("/", "") || localStorage.getItem('omv-uuid') || uuidv4();
const languageId:string = "en-GB"; // Replaced by whatever is in HTML attribute 'data-locale' in views/App
const corsBypass:string[] = ["opuscon.vm"];

localStorage.setItem('omv-uuid', uuid);

/**
	* Instance of global settings
	* Assigned to global._config below (accessed as _config)
*/
var configGlobal:GlobalSettings = {
	UUID: uuid,
	siteIdInURL: urlParams.siteid !== undefined,
	languageId: "",
	lang: undefined,
	crossOrigin: hostMatch(location.hostname, corsBypass) ? null : "anonymous",
	
	// Raster legend dummy layer id
	rastersLegendId: "rasters:0",
	
	// Annotation legend dummy layer id
	annotationLegendId: "annotations:0",
	
	// Ol-hashed
	hashState: {},
	
	/**
		* Set language by ID
		* @param id - id of language to set (e.g. en-GB)
	*/
	setLang: (id:string) => {
		if(_config.languageId == id) return;
		_config.languageId = id;
		_config.lang = getLang(id);
	},
	
	/**
		* Get local storage ID
		* 
		* @remarks
		* The local storage id is based on the map id
	*/
	getLocalStorageId: ():string => {
		return `${_config.getStorageId()}_${_config.UUID}_features`;
	},
	getStorageId: ():number => {
		return _config.storageId || _config.mapId;
	},
	getAssetURL: ():string => {
		let url:string = _config.assetURL || "";
		
		if(url.substring(url.length-1, url.length) == "/")
			url = url.substring(0, url.length-1);
		
		return url;
	},
	
	intersectionChecking: false,
	existingSitesIntersectionChecking: false,
	focusFeaturesOnLoad: true,
	showLayers: true,
	showAllLayersOnStart: true,
	showOpusAttribution: true,
	minimumPolygonArea: 3,
	maximumPolygonArea: 10000000,
	minZoomSnapping: 18,
	focusFeaturePadding: 35,
	minAttrDisplayWidth: 700,
	minAttrDisplayHeight: 200,
	markerDeleteDistance: 250,
	slideTranSpeed: 100,
	defaultBaseLayerIndex: 0,
	scaleUnitOptions: ["metric", "imperial", "degrees", "nautical"],
	toolsEnabledOverride: null,
	toolsDisabledOverride: null,
	drawToolsOverride: null,
	pinDropEnabled: true,
	queryLayersEnabled: true,
	defaultScaleLineUnits: "metric",
	initialToolsEnabled: new Map<MapToolMode, boolean>,
	featureVariationsEnabled: true,
	freehandSimplificationTolerance: 0.1,
	annotationView: false,
	middleAPI: 'https://m.opusmap.co.uk:2053',
	screenshotMap: false,
	screenshotWidth: 100,
	screenshotHeight: 100,
	accessStylingEnabled: false
}

/**
	* Assigns config and events to global
*/
global._config = configGlobal;
global._events = new EventSystem();
global._ui = new UIManager();